import { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { useAppContext } from './ui/layouts/AppContext';
import { AuthGuard } from './ui/components/AuthGuard';
import { AppLayout } from './ui/layouts/AppLayout';
import { ErrorBoundary } from './ui/components/ErrorBoundary';
import { LandingPage } from './ui/pages/LandingPage';
import { LoginPage } from './ui/pages/LoginPage';

const AdminPageRoute = lazy(() =>
  import('./ui/pages/AdminPage').then((m) => ({ default: m.AdminPage })),
);
const SceneGalleryRoute = lazy(() =>
  import('./app/routes/SceneGalleryRoute').then((m) => ({ default: m.SceneGalleryRoute })),
);
const WorldRoute = lazy(() =>
  import('./app/routes/WorldRoute').then((m) => ({ default: m.WorldRoute })),
);
const LevelEditorRoute = lazy(() =>
  import('./app/routes/LevelEditorRoute').then((m) => ({ default: m.LevelEditorRoute })),
);
const SceneComposerRoute = lazy(() =>
  import('./app/routes/SceneComposerRoute').then((m) => ({ default: m.SceneComposerRoute })),
);

function LoadingFallback(): JSX.Element {
  return <div className="flex h-full items-center justify-center">Loading...</div>;
}

function CatchAll(): JSX.Element {
  try {
    const ctx = useAppContext();
    if (ctx.authUser || ctx.isAuthBypass) {
      return <Navigate to="/app" replace />;
    }
  } catch {
    // Not inside AppLayout — fall through to landing
  }
  return <Navigate to="/" replace />;
}

function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route element={<AuthGuard />}>
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<SceneGalleryRoute />} />
              <Route path="admin" element={<AdminPageRoute />} />
              <Route path="scenes/new" element={<SceneComposerRoute />} />
              <Route path="scenes/:sceneId" element={<WorldRoute />} />
              <Route path="scenes/:sceneId/edit" element={<SceneComposerRoute />} />
              <Route path="levels" element={<LevelEditorRoute />} />
              <Route path="levels/:levelId" element={<LevelEditorRoute />} />
            </Route>
          </Route>
          <Route path="*" element={<CatchAll />} />
        </Routes>
      </Suspense>
    </ErrorBoundary>
  );
}

export default App;
